import React, { useState, useEffect } from 'react';
import { Snackbar, Slide, SlideProps } from '@mui/material';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastMessage {
  id: number;
  message: string;
  type: ToastType;
}

// Global variable to hold the addToast function.
let addToast: (message: string, type: ToastType) => void = () => {};

/**
 * Utility function to show a toast message.
 * @param message The message to display.
 * @param type The severity level ('success', 'error', 'info', 'warning'). Default is 'info'.
 */
export const showToast = (message: string, type: ToastType = 'info') => {
  addToast(message, type);
};

// Custom Transition for Snackbar
function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

// Entrance Animation
const slideIn = keyframes`
  from {
    transform: translateX(100%) scale(0.95);
    opacity: 0;
  }
  to {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
`;

// Glassmorphism Styled Components
const ToastCard = styled.div<{ $type: ToastType }>`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-width: 340px;
  max-width: 450px;
  padding: 16px 20px;
  border-radius: 20px;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 20px 40px -10px rgba(0,0,0,0.3), inset 0 1px 1px rgba(255,255,255,0.25);
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: ${({ $type }) => {
    switch ($type) {
      case 'success': return 'linear-gradient(135deg, rgba(16, 185, 129, 0.85) 0%, rgba(5, 150, 105, 0.95) 100%)';
      case 'error': return 'linear-gradient(135deg, rgba(239, 68, 68, 0.85) 0%, rgba(220, 38, 38, 0.95) 100%)';
      case 'warning': return 'linear-gradient(135deg, rgba(245, 158, 11, 0.85) 0%, rgba(217, 119, 6, 0.95) 100%)';
      case 'info':
      default: return 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(37, 99, 235, 0.95) 100%)';
    }
  }};
  color: white;
  animation: ${slideIn} 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  transition: all 0.3s ease;
  font-family: 'Inter', 'Roboto', sans-serif;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.1), 0 4px 8px rgba(0,0,0,0.1);
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 2px;
`;

const Title = styled.h4`
  margin: 0 0 6px 0;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.15);
`;

const Message = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.45;
  opacity: 0.95;
  font-weight: 500;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  opacity: 0.7;
  cursor: pointer;
  padding: 6px;
  margin: -6px -8px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const getIcon = (type: ToastType) => {
  switch (type) {
    case 'success': return <CheckCircle2 size={20} strokeWidth={2.5} />;
    case 'error': return <AlertCircle size={20} strokeWidth={2.5} />;
    case 'warning': return <AlertTriangle size={20} strokeWidth={2.5} />;
    case 'info':
    default: return <Info size={20} strokeWidth={2.5} />;
  }
};

const getTitle = (type: ToastType) => {
  switch (type) {
    case 'success': return 'Success!';
    case 'error': return 'Error!';
    case 'warning': return 'Warning!';
    case 'info':
    default: return 'Notice';
  }
};

export const Toaster: React.FC = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

useEffect(() => {
  addToast = (message: string, type: ToastType) => {
    const id = Date.now();

    // Add new toast
    setToasts((prev) => [...prev, { id, message, type }]);

    // Automatically remove after 5 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 5000);
  };
}, []);

  const handleClose = (id: number) => (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <>
      {toasts.map((toast, index) => (
        <Snackbar
          key={toast.id}
          open={true}
          // autoHideDuration={4000}
          onClose={handleClose(toast.id)}
          TransitionComponent={SlideTransition}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          style={{ top: `${32 + (index * 100)}px`, right: '32px', zIndex: 9999 }}
        >
          <ToastCard $type={toast.type}>
            <IconWrapper>{getIcon(toast.type)}</IconWrapper>
            <ContentWrapper>
              <Title>{getTitle(toast.type)}</Title>
              <Message>{toast.message}</Message>
            </ContentWrapper>
            <CloseButton onClick={handleClose(toast.id)} aria-label="Close">
              <X size={18} strokeWidth={2.5} />
            </CloseButton>
          </ToastCard>
        </Snackbar>
      ))}
    </>
  );
};
